import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Avatar from 'components/base/Avatar';
import Badge, { BadgeBg } from 'components/base/Badge';
import Button from 'components/base/Button';
import { Link } from 'react-router-dom';
import { User } from 'services/types';

const getStatusBadge = (status: string): BadgeBg => {
  switch (status) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'warning';
    case 'suspended':
      return 'danger';
    default:
      return 'secondary';
  }
};

const getUsersTableColumns = (
  onEditUser: (user: User) => void
): ColumnDef<User>[] => [
  {
    accessorKey: 'name',
    header: 'User',
    cell: ({ row: { original } }) => {
      const { name, avatar, id } = original;
      return (
        <Link
          to={`/app/users/details/${id}`}
          className="d-flex align-items-center text-1100"
        >
          <Avatar src={avatar} size="m" />
          <p className="mb-0 ms-3 fw-bold">{name}</p>
        </Link>
      );
    },
    meta: {
      headerProps: { style: { width: '18%' }, className: 'pe-5 ps-0' },
      cellProps: { className: 'py-0 pe-5 ps-0 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'username',
    header: 'Username',
    cell: ({ row: { original } }) => {
      const { username } = original;
      return (
        <Link to={`mailto:${username}`} className="fw-semi-bold">
          {username}
        </Link>
      );
    },
    meta: {
      headerProps: { style: { width: '22%' }, className: 'pe-5 ps-1' },
      cellProps: { className: 'pe-5 ps-1' }
    }
  },
  {
    accessorKey: 'role',
    header: 'Role',
    meta: {
      headerProps: { style: { width: '12%' }, className: 'pe-5 ps-1' },
      cellProps: { className: 'fw-semi-bold text-1000 pe-5 ps-1' }
    }
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row: { original } }) => {
      const { status } = original;
      return (
        <Badge variant="phoenix" bg={getStatusBadge(status ?? '')}>
          {status
            ? status.charAt(0).toUpperCase() + status.slice(1)
            : 'Unknown'}
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { width: '12%' }, className: 'pe-5 ps-1' },
      cellProps: { className: 'pe-5 ps-1' }
    }
  },
  {
    accessorKey: 'created_at',
    header: 'Created At',
    cell: ({ row: { original } }) => {
      const { created_at } = original;
      return (
        <span className="text-700">
          {created_at ? new Date(created_at).toLocaleDateString() : 'N/A'}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '11%' }, className: 'pe-5 ps-1' },
      cellProps: { className: 'text-700 pe-5 ps-1' }
    }
  },
  {
    accessorKey: 'updated_at',
    header: 'Updated At',
    cell: ({ row: { original } }) => {
      const { updated_at } = original;
      return (
        <span className="text-700">
          {updated_at ? new Date(updated_at).toLocaleDateString() : 'N/A'}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '11%' }, className: 'pe-5 ps-1' },
      cellProps: { className: 'text-700 pe-5 ps-1' }
    }
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row: { original } }) => {
      return (
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => onEditUser(original)}
          className="me-2"
        >
          <FontAwesomeIcon icon={faEdit} className="me-1" />
          Edit
        </Button>
      );
    },
    meta: {
      headerProps: { style: { width: '14%' }, className: 'text-end px-1' },
      cellProps: { className: 'text-end px-1' }
    }
  }
];

const UsersTable = () => {
  return (
    <div>
      <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
      <AdvanceTableFooter pagination />
    </div>
  );
};

export { getUsersTableColumns };
export default UsersTable;
